import { Injectable } from '@nestjs/common';
import { CheckoutAddress, OrderSummary, OrderStatusHistoryEntry } from '@cart/shared-types';
import { variantLabel } from '@cart/shared-utils';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService } from '../tenancy/store-context.service';

export abstract class OrdersRepository {
  abstract list(userId?: string): Promise<OrderSummary[]>;
  abstract findById(orderId: string, userId?: string): Promise<OrderSummary | undefined>;
  abstract create(order: OrderSummary, userId?: string): Promise<OrderSummary>;
  abstract updateStatus(orderId: string, status: OrderSummary['status']): Promise<OrderSummary | undefined>;
}

/**
 * Narrows Prisma's free-form optionSnapshot JSON to the flat string map the
 * label helper expects. Anything else is dropped rather than rendered — a
 * nested object would print as "[object Object]" on an order line.
 */
function toOptionMap(snapshot: unknown): Record<string, string> | null {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null;
  const entries = Object.entries(snapshot as Record<string, unknown>)
    .filter(([key, value]) => key.trim() && (typeof value === 'string' || typeof value === 'number'))
    .map(([key, value]) => [key, String(value)] as const);
  return entries.length ? Object.fromEntries(entries) : null;
}

@Injectable()
export class InMemoryOrdersRepository implements OrdersRepository {
  private readonly orders: OrderSummary[] = [];

  async list(_userId?: string): Promise<OrderSummary[]> {
    return this.orders.map((order) => ({
      ...order,
      items: order.items.map((item) => ({ ...item })),
    }));
  }

  async create(order: OrderSummary, _userId?: string): Promise<OrderSummary> {
    this.orders.push({
      ...order,
      items: order.items.map((item) => ({ ...item })),
    });

    return {
      ...order,
      items: order.items.map((item) => ({ ...item })),
    };
  }

  async findById(orderId: string, _userId?: string): Promise<OrderSummary | undefined> {
    const order = this.orders.find((item) => item.id === orderId);

    if (!order) {
      return undefined;
    }

    return {
      ...order,
      items: order.items.map((item) => ({ ...item })),
    };
  }

  async updateStatus(
    orderId: string,
    status: OrderSummary['status']
  ): Promise<OrderSummary | undefined> {
    const order = this.orders.find((item) => item.id === orderId);

    if (!order) {
      return undefined;
    }

    order.status = status;

    return {
      ...order,
      items: order.items.map((item) => ({ ...item })),
    };
  }
}

@Injectable()
export class PrismaOrdersRepository implements OrdersRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService
  ) {}

  async list(userId?: string): Promise<OrderSummary[]> {
    const orders = await this.prisma.order.findMany({
      where: {
        storeId: this.storeId(),
        ...(userId ? { customerId: userId } : {}),
      },
      orderBy: { createdAt: 'desc' },
    });

    if (!orders.length) return [];

    const orderIds = orders.map((o) => o.id);
    const customerIds = [
      ...new Set(orders.map((o) => o.customerId).filter((id): id is string => id !== null)),
    ];

    const [allItems, customers] = await Promise.all([
      this.prisma.orderItem.findMany({
        where: { storeId: this.storeId(), orderId: { in: orderIds } },
        orderBy: { createdAt: 'asc' },
      }),
      customerIds.length
        ? this.prisma.user.findMany({
            where: { id: { in: customerIds } },
            select: { id: true, name: true, email: true },
          })
        : Promise.resolve([]),
    ]);

    const itemsByOrder = new Map<string, typeof allItems>();
    for (const item of allItems) {
      const list = itemsByOrder.get(item.orderId) ?? [];
      list.push(item);
      itemsByOrder.set(item.orderId, list);
    }

    const customerMap = new Map(customers.map((c) => [c.id, c]));
    const imagesByProduct = await this.imagesByProduct(allItems.map((i) => i.productId));
    // Resolved once for the whole page rather than per order — a list of 50
    // orders would otherwise fire 50 variant queries.
    const variantLabels = await this.variantLabels(allItems);

    // No userId = an admin call (see the two controllers — the customer
    // route always passes request.user.id, the admin route never does).
    // tcsTotal is an admin-only accounting figure — see the GstTotals doc
    // comment in shared-types — so it's only attached on that branch.
    const isAdmin = userId === undefined;

    return orders.map((order) => {
      const items = itemsByOrder.get(order.id) ?? [];
      const customer = order.customerId ? customerMap.get(order.customerId) : null;
      return this.mapOrder(order, items, customer ?? null, undefined, imagesByProduct, variantLabels, isAdmin);
    });
  }

  async findById(orderId: string, userId?: string): Promise<OrderSummary | undefined> {
    const order = await this.prisma.order.findFirst({
      where: {
        id: orderId,
        storeId: this.storeId(),
        ...(userId ? { customerId: userId } : {}),
      },
    });

    if (!order) return undefined;

    const [items, history, customer] = await Promise.all([
      this.prisma.orderItem.findMany({
        where: { storeId: this.storeId(), orderId },
        orderBy: { createdAt: 'asc' },
      }),
      this.prisma.orderStatusHistory.findMany({
        where: { storeId: this.storeId(), orderId },
        orderBy: { createdAt: 'asc' },
      }),
      order.customerId
        ? this.prisma.user.findFirst({
            where: { id: order.customerId },
            select: { name: true, email: true },
          })
        : Promise.resolve(null),
    ]);

    const imagesByProduct = await this.imagesByProduct(items.map((i) => i.productId));

    const variantLabels = await this.variantLabels(items);
    return this.mapOrder(order, items, customer, history, imagesByProduct, variantLabels, userId === undefined);
  }

  /**
   * Display labels for the variants an order references.
   *
   * Skipped entirely when no line carries a variant, which is every order in
   * a catalogue that sells one size of everything.
   */
  private async variantLabels(
    items: Array<{ variantId: string | null }>
  ): Promise<Map<string, string> | undefined> {
    const ids = [...new Set(items.map((item) => item.variantId).filter((id): id is string => !!id))];
    if (!ids.length) return undefined;

    const variants = await this.prisma.productVariant.findMany({
      where: { id: { in: ids }, storeId: this.storeId() },
      select: {
        id: true,
        title: true,
        unitQuantity: true,
        unitOfMeasure: true,
        optionSnapshot: true,
      },
    });

    return new Map(
      variants.map((variant) => [
        variant.id,
        variantLabel({
          options: toOptionMap(variant.optionSnapshot),
          unitQuantity: variant.unitQuantity,
          unitOfMeasure: variant.unitOfMeasure,
          title: variant.title,
        }),
      ])
    );
  }

  private async imagesByProduct(productIds: string[]): Promise<Map<string, string>> {
    const uniqueIds = [...new Set(productIds)];
    if (!uniqueIds.length) return new Map();

    const images = await this.prisma.productImage.findMany({
      where: { storeId: this.storeId(), productId: { in: uniqueIds }, isActive: true },
      orderBy: { sortOrder: 'asc' },
      select: { productId: true, url: true },
    });

    const map = new Map<string, string>();
    for (const image of images) {
      if (!map.has(image.productId)) map.set(image.productId, image.url);
    }
    return map;
  }

  async create(order: OrderSummary, userId?: string): Promise<OrderSummary> {
    await this.prisma.$transaction([
      this.prisma.order.create({
        data: {
          id: order.id,
          storeId: this.storeId(),
          customerId: userId,
          orderNumber: order.id,
          status: order.status,
          paymentStatus: 'pending',
          fulfillmentStatus: 'unfulfilled',
          currency: 'INR',
          subtotal: order.subtotal,
          discountTotal: order.discountTotal ?? 0,
          grandTotal: order.grandTotal ?? order.subtotal,
          placedOn: new Date(order.createdAt),
        },
      }),
      ...order.items.map((item) =>
        this.prisma.orderItem.create({
          data: {
            id: item.id,
            storeId: this.storeId(),
            orderId: order.id,
            productId: item.productId,
            title: item.productName,
            sku: item.productSlug,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
          },
        })
      ),
      this.prisma.orderStatusHistory.create({
        data: {
          storeId: this.storeId(),
          orderId: order.id,
          toStatus: order.status,
        },
      }),
    ]);

    return (await this.findById(order.id)) ?? order;
  }

  async updateStatus(
    orderId: string,
    status: OrderSummary['status']
  ): Promise<OrderSummary | undefined> {
    const existing = await this.prisma.order.findFirst({
      where: { id: orderId, storeId: this.storeId() },
      select: { id: true, status: true },
    });

    if (!existing) {
      return undefined;
    }

    await this.prisma.$transaction([
      this.prisma.order.update({
        where: { id: orderId },
        data: { status },
      }),
      this.prisma.orderStatusHistory.create({
        data: {
          storeId: this.storeId(),
          orderId,
          fromStatus: existing.status,
          toStatus: status,
        },
      }),
    ]);

    return this.findById(orderId);
  }

  private mapOrder(
    order: {
      id: string;
      orderNumber: string;
      status: string;
      customerId: string | null;
      email: string | null;
      paymentStatus: string;
      fulfillmentStatus: string;
      subtotal: number;
      taxTotal: number;
      cgstTotal: number;
      sgstTotal: number;
      igstTotal: number;
      tcsTotal: number;
      shippingTotal: number;
      discountTotal: number;
      grandTotal: number;
      shippingAddress: unknown;
      createdAt: Date;
    },
    items: Array<{
      id: string;
      orderId: string;
      productId: string;
      variantId: string | null;
      title: string;
      sku: string | null;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
      hsnCode: string | null;
      igstRateBps: number;
      cgstRateBps: number;
      sgstRateBps: number;
      isInterState: boolean;
      taxableValue: number;
      cgstAmount: number;
      sgstAmount: number;
      igstAmount: number;
    }>,
    customer: { name: string; email: string } | null | undefined,
    history: Array<{ fromStatus: string | null; toStatus: string; createdAt: Date }> | undefined,
    imagesByProduct: Map<string, string> | undefined,
    /** Variant id -> display label, resolved by the caller. */
    variantLabelsById: Map<string, string> | undefined,
    // Admin-only — see the GstTotals doc comment in shared-types. tcsTotal
    // is real Indian GST TCS (Section 52): withheld from a seller's own
    // payout, never added to what the customer pays, so it must never
    // appear on the customer-facing read path.
    isAdmin: boolean
  ): OrderSummary {
    const statusHistory: OrderStatusHistoryEntry[] | undefined = history
      ? history.map((h) => ({
          fromStatus: h.fromStatus ?? undefined,
          toStatus: h.toStatus,
          createdAt: h.createdAt.toISOString(),
        }))
      : undefined;

    return {
      id: order.id,
      orderNumber: order.orderNumber,
      status: order.status as OrderSummary['status'],
      customerId: order.customerId ?? undefined,
      customerName: customer?.name,
      customerEmail: customer?.email ?? order.email ?? undefined,
      paymentStatus: order.paymentStatus,
      fulfillmentStatus: order.fulfillmentStatus,
      subtotal: order.subtotal,
      taxTotal: order.taxTotal,
      cgstTotal: order.cgstTotal,
      sgstTotal: order.sgstTotal,
      igstTotal: order.igstTotal,
      ...(isAdmin ? { tcsTotal: order.tcsTotal } : {}),
      shippingTotal: order.shippingTotal,
      discountTotal: order.discountTotal,
      grandTotal: order.grandTotal,
      shippingAddress: (order.shippingAddress ?? undefined) as CheckoutAddress | undefined,
      items: items.map((item) => ({
        id: item.id,
        productId: item.productId,
        productName: item.title,
        productSlug: item.sku ?? item.productId,
        productImageUrl: imagesByProduct?.get(item.productId),
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        lineTotal: item.lineTotal,
        variantId: item.variantId ?? null,
        // Read back from the variant rather than frozen onto the line: a
        // merchant fixing "1 Ltr" to "1 L" should not leave old orders
        // showing the typo. The price stays whatever was charged.
        variantLabel: item.variantId ? variantLabelsById?.get(item.variantId) ?? null : null,
        hsnCode: item.hsnCode ?? undefined,
        igstRateBps: item.igstRateBps,
        cgstRateBps: item.cgstRateBps,
        sgstRateBps: item.sgstRateBps,
        isInterState: item.isInterState,
        taxableValue: item.taxableValue,
        cgstAmount: item.cgstAmount,
        sgstAmount: item.sgstAmount,
        igstAmount: item.igstAmount,
      })),
      totalItems: items.reduce((sum, item) => sum + item.quantity, 0),
      createdAt: order.createdAt.toISOString(),
      ...(statusHistory !== undefined ? { statusHistory } : {}),
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}
